// Scroll snap for product card galleries, held only for the length of a gesture.
//
// A mandatory snap container is re-resolved by the browser itself whenever the page is laid out
// again, and on WebKit that lands the scroller on a different slide: on a collection page half the
// grid ends up showing a photo nobody chose, with nothing writing to those scrollers from script
// (proved by instrumenting `scrollLeft` and `scrollTo` and getting no writes while eleven of
// twenty-four galleries had moved).
//
// Snapping is worth keeping while a finger or a pointer is actually dragging, since a card is
// narrow enough that a loose swipe would rest between two photos. So the attribute below is put on
// at the start of the gesture and taken off once the scrolling has settled, which leaves nothing
// for the browser to re-resolve the rest of the time. The pair of rules lives in
// snippets/product-card-standalone-gallery-styles.liquid.
//
// Delegated from the document, in the capture phase because `scroll` does not bubble, so this is
// indifferent to when cards arrive: filtering, infinite scroll and the Algolia hits all work.

const SNAPPING_ATTRIBUTE = 'data-snapping';
const SCROLLER_SELECTOR = '.card-gallery slideshow-slides';
const SETTLED_AFTER = 150;

/** @type {WeakMap<Element, number>} */
const settleTimers = new WeakMap();

/** @param {Event} event */
function startSnapping(event) {
  const target = event.target;
  if (!(target instanceof Element)) return;

  const scroller = target.closest(SCROLLER_SELECTOR);
  if (scroller) scroller.setAttribute(SNAPPING_ATTRIBUTE, '');
}

/** @param {Event} event */
function releaseWhenSettled(event) {
  const scroller = event.target;
  if (!(scroller instanceof Element) || !scroller.matches(SCROLLER_SELECTOR)) return;
  if (!scroller.hasAttribute(SNAPPING_ATTRIBUTE)) return;

  // Released on the quiet rather than on the finger lifting: the scroll carries on under its own
  // momentum after that, and the snap has to still be in force when it comes to rest.
  clearTimeout(settleTimers.get(scroller));
  settleTimers.set(
    scroller,
    window.setTimeout(() => scroller.removeAttribute(SNAPPING_ATTRIBUTE), SETTLED_AFTER),
  );
}

document.addEventListener('pointerdown', startSnapping, { capture: true, passive: true });
document.addEventListener('scroll', releaseWhenSettled, { capture: true, passive: true });
